import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useSocket } from '../contexts/SocketContext';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { X, MessageSquare, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PERM_ADMIN = 1 << 8;
const PERM_USE_MEDIA = 1 << 9;
const EMOJI_REGEX = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
const GIF_REGEX = /(https?:\/\/\S+\.(gif|webp)|tenor\.com|giphy\.com)/i;
const containsMedia = (text) => Boolean(text && (EMOJI_REGEX.test(text) || GIF_REGEX.test(text)));

export const ThreadPanel = ({ threadId, parentMessage, onClose }) => {
  const { user, token } = useAuth();
  const { userPerms } = useApp();
  const { on } = useSocket();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [mediaError, setMediaError] = useState('');
  const canUseMedia = Boolean(userPerms & PERM_USE_MEDIA) || Boolean(userPerms & PERM_ADMIN);
  const scrollRef = useRef(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  const fetchThreadMessages = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/threads/${threadId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMessages(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [threadId, token]);

  useEffect(() => { fetchThreadMessages(); }, [fetchThreadMessages]);

  useEffect(() => {
    if (!on) return;
    const unsub = on('THREAD_MESSAGE_CREATE', (e) => {
      if (e.data.thread_id === threadId) {
        setMessages(prev => {
          if (prev.some(m => m.message_id === e.data.message_id)) return prev;
          return [...prev, e.data];
        });
      }
    });
    return () => unsub?.();
  }, [on, threadId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    const content = newMsg;
    if (containsMedia(content) && !canUseMedia) {
      setMediaError('Nu ai permisiunea pentru emoji/GIF/sticker.');
      return;
    }
    setNewMsg('');
    setMediaError('');

    // Optimistic
    const temp = {
      message_id: `temp_${Date.now()}`,
      thread_id: threadId,
      content,
      user_id: user.user_id,
      user: { user_id: user.user_id, username: user.username, display_name: user.display_name, avatar: user.avatar },
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, temp]);

    try {
      const res = await fetch(`${API_URL}/api/threads/${threadId}/messages`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ content })
      });
      if (res.ok) {
        const real = await res.json();
        setMessages(prev => prev.map(m => m.message_id === temp.message_id ? { ...real, user: real.user || temp.user } : m));
      } else {
        setMessages(prev => prev.filter(m => m.message_id !== temp.message_id));
        const err = await res.json().catch(() => ({}));
        setMediaError(err.detail || 'Nu ai permisiunea pentru emoji/GIF/sticker.');
      }
    } catch {
      setMessages(prev => prev.filter(m => m.message_id !== temp.message_id));
    }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="w-80 h-full bg-[#12141a] border-l border-white/5 flex flex-col flex-shrink-0" data-testid="thread-panel">
      {/* Header */}
      <div className="h-12 px-4 flex items-center justify-between border-b border-white/5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-zinc-400" />
          <span className="text-sm font-semibold text-white">Thread</span>
        </div>
        <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded hover:bg-white/10" data-testid="close-thread">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Parent message */}
      {parentMessage && (
        <div className="px-4 py-3 border-b border-white/5 bg-[#0e1015]">
          <div className="flex items-center gap-2 mb-1">
            <Avatar className="h-5 w-5">
              <AvatarImage src={parentMessage.user?.avatar} />
              <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-[8px]">
                {(parentMessage.user?.display_name || parentMessage.user?.username || '?')[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-semibold text-white">{parentMessage.user?.display_name || parentMessage.user?.username}</span>
            <span className="text-[10px] text-zinc-500">{formatTime(parentMessage.created_at)}</span>
          </div>
          <p className="text-xs text-zinc-300 line-clamp-3">{parentMessage.content}</p>
        </div>
      )}

      {/* Thread messages */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="p-3 space-y-2">
          {loading && messages.length === 0 && (
            <p className="text-xs text-zinc-600 text-center py-4">Loading thread...</p>
          )}
          {!loading && messages.length === 0 && (
            <p className="text-xs text-zinc-600 text-center py-4">No replies yet. Start the conversation!</p>
          )}
          {messages.map(m => {
            const embeds = extractEmbeds(m.content);
            return (
              <div key={m.message_id} className="flex gap-2" data-testid={`thread-msg-${m.message_id}`}>
                <Avatar className="h-6 w-6 mt-0.5 flex-shrink-0">
                  <AvatarImage src={m.user?.avatar} />
                  <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-[8px]">
                    {(m.user?.display_name || m.user?.username || '?')[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs font-semibold text-white">{m.user?.display_name || m.user?.username}</span>
                    <span className="text-[10px] text-zinc-600">{formatTime(m.created_at)}</span>
                  </div>
                  <p className="text-xs text-zinc-300 break-words">{m.content}</p>
                  {embeds.length > 0 && (
                    <div className="mt-2 space-y-2" data-testid={`thread-embeds-${m.message_id}`}>
                      {embeds.map((embed, idx) => (
                        <div key={`${embed.type}-${idx}`} data-testid={`thread-embed-${m.message_id}-${idx}`}>
                          {embed.type === 'image' && (
                            <img src={embed.url} alt="embed" className="max-w-full rounded-lg border border-white/10" />
                          )}
                          {embed.type === 'youtube' && (
                            <div className="w-full aspect-video rounded-lg overflow-hidden border border-white/10">
                              <iframe
                                title={embed.id}
                                src={`https://www.youtube.com/embed/${embed.id}`}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          )}
                          {embed.type === 'link' && (
                            <a
                              href={embed.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block bg-[#12141a] border border-white/10 rounded-lg p-3 hover:border-cyan-500/30 transition-colors"
                            >
                              <p className="text-xs text-zinc-500">{embed.domain}</p>
                              <p className="text-xs text-cyan-300 truncate" data-testid={`thread-embed-link-${m.message_id}-${idx}`}>{embed.url}</p>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {mediaError && (
        <div className="px-4 py-1 text-xs text-red-400 bg-red-500/10 border-t border-white/5" data-testid="thread-media-permission-alert">
          {mediaError}
        </div>
      )}

      {/* Input */}
      <form onSubmit={sendReply} className="px-3 py-2 border-t border-white/5 flex-shrink-0">
        <div className="flex items-center gap-2 bg-[#0b0d10] rounded-lg px-3 py-2 border border-white/5">
          <input
            value={newMsg}
            onChange={e => {
              const value = e.target.value;
              setNewMsg(value);
              if (mediaError && (canUseMedia || !containsMedia(value))) setMediaError('');
            }}
            placeholder="Reply..."
            className="flex-1 bg-transparent text-xs text-white placeholder-zinc-600 outline-none"
            data-testid="thread-input"
          />
          <button type="submit" disabled={!newMsg.trim()}
            className="text-zinc-500 hover:text-cyan-400 disabled:opacity-30 transition-colors" data-testid="thread-send-btn">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </form>
    </div>
  );
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const IMAGE_REGEX = /\.(png|jpe?g|gif|webp)$/i;

function extractEmbeds(text) {
  if (!text) return [];
  const urls = (text.match(URL_REGEX) || [])
    .map(url => url.replace(/[),.]+$/, ''));
  const unique = Array.from(new Set(urls));

  return unique.map(url => {
    const youtubeId = getYoutubeId(url);
    if (youtubeId) return { type: 'youtube', url, id: youtubeId };
    if (IMAGE_REGEX.test(url)) return { type: 'image', url };
    return { type: 'link', url, domain: getDomain(url) };
  });
}

function getYoutubeId(url) {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      return parsed.pathname.replace('/', '');
    }
    if (parsed.hostname.includes('youtube.com')) {
      return parsed.searchParams.get('v');
    }
    return null;
  } catch {
    return null;
  }
}

function getDomain(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}
